import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { keyframes } from '@mui/material/styles';

export type Hotness = 'cool' | 'warm' | 'hot' | 'fire';

const REEL_ITEMS = 6; // 5 random + 1 final
const ITEM_HEIGHT_EM = 1.2;
const REEL_DURATION_MS = 500;
const POP_DURATION_MS = 200;
const FLASH_DURATION_MS = 550;

const reel = keyframes`
    from { transform: translateY(0); }
    to   { transform: translateY(-${(REEL_ITEMS - 1) * ITEM_HEIGHT_EM}em); }
`;

const pop = keyframes`
    0%   { transform: scale(1); }
    50%  { transform: scale(1.18); }
    100% { transform: scale(1); }
`;

// Brightness pulse that fires as the reel lands.
const flash = keyframes`
    0%   { filter: brightness(1); }
    25%  { filter: brightness(1.3); }
    100% { filter: brightness(1); }
`;

const HOTNESS_SX: Record<Hotness, object> = {
    cool: { color: 'text.primary' },
    warm: { color: 'warning.main' },
    hot: { color: 'secondary.main' },
    fire: { color: 'primary.main', fontWeight: 700 },
};

export function hotnessForMinBet(value: number | null | undefined): Hotness {
    if (value == null) return 'cool';
    if (value <= 9) return 'fire';
    if (value <= 14) return 'hot';
    if (value <= 24) return 'warm';
    return 'cool';
}

function randomDigits(): string[] {
    const out: string[] = [];
    for (let i = 0; i < REEL_ITEMS - 1; i++) {
        out.push(String(Math.floor(Math.random() * 90) + 10));
    }
    return out;
}

interface Props {
    value: number | null | undefined,
    format?: (n: number) => string,
    emptyText?: string,
    hotness?: Hotness,
    showFireEmoji?: boolean,
}

/**
 * Slot-machine style reel that animates to a new numeric value when `value`
 * changes. First render is static (no animation). Honors prefers-reduced-motion.
 */
export default function SlotNumber({
    value,
    format = (n) => String(n),
    emptyText = '-',
    hotness = 'cool',
    showFireEmoji = false,
}: Props) {
    const prevRef = useRef<number | null | undefined>(value);
    const [animating, setAnimating] = useState(false);
    const [reelValues] = useState<string[]>(() => randomDigits());
    const [tickValues, setTickValues] = useState<string[]>(reelValues);

    const reduced = typeof window !== 'undefined'
        && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    useEffect(() => {
        const prev = prevRef.current;
        prevRef.current = value;
        if (prev === value) return;
        if (prev === undefined) return; // first render
        if (value == null) return; // no reel for "no value"
        if (reduced) return;
        setTickValues(randomDigits());
        setAnimating(true);
        const t = window.setTimeout(
            () => setAnimating(false),
            REEL_DURATION_MS + Math.max(POP_DURATION_MS, FLASH_DURATION_MS) + 50,
        );
        return () => window.clearTimeout(t);
    }, [value, reduced]);

    const text = value == null ? emptyText : format(value);
    const hotnessSx = value == null ? {} : HOTNESS_SX[hotness];
    const fire = showFireEmoji && hotness === 'fire' && value != null;

    if (!animating) {
        return (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, ...hotnessSx }}>
                {fire && <span aria-hidden>🔥</span>}
                <span>{text}</span>
            </Box>
        );
    }

    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                animation: `${flash} ${FLASH_DURATION_MS}ms ease-out ${REEL_DURATION_MS}ms`,
                ...hotnessSx,
            }}
        >
            {fire && <span aria-hidden>🔥</span>}
            <Box
                component="span"
                sx={{
                    display: 'inline-block',
                    height: `${ITEM_HEIGHT_EM}em`,
                    overflow: 'hidden',
                    verticalAlign: 'bottom',
                    lineHeight: `${ITEM_HEIGHT_EM}em`,
                    animation: `${pop} ${POP_DURATION_MS}ms ease-out ${REEL_DURATION_MS}ms`,
                }}
            >
                <Box
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        flexDirection: 'column',
                        animation: `${reel} ${REEL_DURATION_MS}ms cubic-bezier(.2,.7,.2,1) forwards`,
                    }}
                >
                    {tickValues.map((v, i) => (
                        <span key={i} style={{ height: `${ITEM_HEIGHT_EM}em`, lineHeight: `${ITEM_HEIGHT_EM}em` }}>{v}</span>
                    ))}
                    <span style={{ height: `${ITEM_HEIGHT_EM}em`, lineHeight: `${ITEM_HEIGHT_EM}em` }}>{text}</span>
                </Box>
            </Box>
        </Box>
    );
}
